import {useEffect, useState, useCallback, useContext, useMemo, useRef} from "react";
import styled from "styled-components";
import PropTypes from "prop-types";
import dayjs from "dayjs";
import {FormProvider, useForm} from "react-hook-form";
import {faFileExport} from "@fortawesome/free-solid-svg-icons";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";

// Hooks
import useApi from "../../hooks/useApi.js";

// Contexts
import {FacilityNavContext} from "../../contexts/facilitynav.js";
import {SettingsContext} from "../../contexts/settings.js";

// Components
import Modal from "../../components/Modal.js";
import BaseTable from "../../components/BaseTable.js";
import Pagination from "../../components/Pagination.js";
import InputDate from "../../components/form/InputDate.js";

// Utils
import {addCommas, prettyDateInUserTimezone, slugify} from "../../utils/helpers.js";
import startExport from "../../utils/export.js";
import {getWeatherSourceLinks, LIVE_WEATHER_SOURCE_TITLE} from "../../utils/weather-links.js";

// Style
import {
  AnchorInline,
  Heading,
  Inline,
  Button,
  Abbr,
  Small,
  TableWrapper,
  TableHeaderWrap,
  TableFooter,
  Text
} from "../../style/components/general.js";
import {pad, radius} from "../../style/components/variables.js";
import {bp} from "../../style/components/breakpoints.js";

const PAGE_LIMIT = 10;
const MODAL_MAX_WIDTH = "min(1024px, 90vw)";

const HEADINGS = {
  date: {header: "Date"},
  tempMin: {header: "Min Temp"},
  tempMax: {header: "Max Temp"},
  tempAvg: {header: "Avg Temp"},
  rainfall: {header: "Rainfall"},
  cumulative: {header: "Total Rainfall"},
  precipitationChance: {header: "Precip Chance"},
  wind: {header: "Wind"}
};

const ModalWeatherTable = ({visible, setVisible}) => {
  const [stats, setStats] = useState();
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const fetchId = useRef(0);

  const form = useForm();
  const {watch} = form;

  const {facility} = useContext(FacilityNavContext);
  const {settings} = useContext(SettingsContext);
  // Rows are anchored to calendar days in the facility's weather timezone (API falls back the same way).
  const weatherTimezone = facility?.weatherSource?.timezone || settings?.timezone;
  const weatherSourceLinks = getWeatherSourceLinks({
    ...facility?.primaryAddress,
    weatherSource: facility?.weatherSource
  });

  const startDate = watch("startDate");
  const endDate = watch("endDate");

  const {api: apiWeather} = useApi("facility-weather");

  const fetchStats = useCallback(() => {
    if (!facility?.primaryAddress) {
      setStats([]);
      setLoading(false);
      return;
    }

    const requestId = ++fetchId.current;
    setLoading(true);
    const params = {facilityId: facility.id};
    if (startDate) params.startDate = dayjs(startDate).format("YYYY-MM-DD");
    if (endDate) params.endDate = dayjs(endDate).format("YYYY-MM-DD");

    apiWeather
      .callGet(undefined, params)
      .then(res => {
        if (requestId !== fetchId.current) return;
        if (res?.status === 200) {
          setStats(res.data);
          setCurrentPage(1);
        }
      })
      .finally(() => {
        if (requestId === fetchId.current) setLoading(false);
      });
  }, [startDate, endDate, apiWeather, facility]);

  useEffect(() => {
    if (visible) fetchStats();
  }, [visible, fetchStats]);

  const weatherExport = useCallback(() => {
    const params = {facilityId: facility.id};
    if (startDate) params.startDate = dayjs(startDate).format("YYYY-MM-DD");
    if (endDate) params.endDate = dayjs(endDate).format("YYYY-MM-DD");

    apiWeather.callPatch(null, params).then(res => {
      const {status, data} = res;
      if (status === 200 && data?.rows) {
        const rows = [];
        data.rows.map(row => {
          const temp = {};
          row.map(pair => {
            temp[pair.key] = pair.value;
          });
          rows.push(temp);
        });

        startExport("csv", "weather_export", rows);
      }
    });
  }, [apiWeather, facility, startDate, endDate]);

  const tableData = useMemo(() => {
    if (!stats) return null;
    return stats.map(item => {
      const wind = [item.windDirection, item.windSpeed].filter(Boolean).join(" ");
      return {
        ...Object.fromEntries(
          Object.entries(item).map(([key, value]) => {
            if (["tempMin", "tempMax", "tempAvg"].includes(key))
              return [key, value != null ? `${value} °F` : "—"];
            if (key === "precipitationChance")
              return [key, value != null ? `${Math.round(value)}%` : "—"];
            if (["rainfall", "cumulative"].includes(key))
              return [
                key,
                value != null ? `${addCommas(value, 2)}${value > 0 ? " in." : ""}` : "—"
              ];
            return [key, value];
          })
        ),
        wind: wind || "—",
        date: prettyDateInUserTimezone(item.date, weatherTimezone, "MM/DD/YYYY")
      };
    });
  }, [stats, weatherTimezone]);

  const total = tableData?.length || 0;
  const pageTotal = Math.max(1, Math.ceil(total / PAGE_LIMIT));
  const pagedData = useMemo(() => {
    if (!tableData) return null;
    const start = (currentPage - 1) * PAGE_LIMIT;
    return tableData.slice(start, start + PAGE_LIMIT);
  }, [tableData, currentPage]);

  return (
    <Modal visible={visible} setVisible={setVisible} maxWidth={MODAL_MAX_WIDTH}>
      <TableWrapper>
        <FormProvider {...form}>
          <TableHeaderWrap>
            <HeaderRow>
              <TitleGroup>
                <Title smaller>Weather History</Title>
                {facility?.weatherSource && (
                  <StationNote
                    secondary
                    title={`${facility.weatherSource.stationName} (${facility.weatherSource.distanceMiles} mi)`}>
                    (Station:&nbsp;<StationName>{facility.weatherSource.stationName}</StationName>
                    ,&nbsp;{facility.weatherSource.distanceMiles} mi)
                  </StationNote>
                )}
              </TitleGroup>
              <Controls>
                <DatePart>
                  from
                  <InputDate name="startDate" inline />
                </DatePart>
                <DatePart>
                  to
                  <InputDate name="endDate" inline />
                </DatePart>
                {facility?.primaryAddress && (
                  <Export type="button" onClick={() => weatherExport()}>
                    <Abbr title="Export">
                      <FontAwesomeIcon icon={faFileExport} />
                    </Abbr>
                  </Export>
                )}
              </Controls>
            </HeaderRow>
          </TableHeaderWrap>
        </FormProvider>

        <BaseTable
          headings={HEADINGS}
          data={pagedData}
          loading={loading}
          dataMissing="No weather history for this facility."
          cellFullWidth
        />

        <TableFooter>
          <FooterLeft>
            {total > 0 && <Text quiet>{total} total</Text>}
            {weatherSourceLinks.map(({label, url}) => (
              <AnchorInline
                key={label}
                href={url}
                target="_blank"
                rel="noopener noreferrer"
                title={LIVE_WEATHER_SOURCE_TITLE}
                data-testid={`weather-source-${slugify(label)}`}>
                {label}
              </AnchorInline>
            ))}
          </FooterLeft>
          {total > 0 && pageTotal > 1 && (
            <Pagination
              current={currentPage}
              setCurrent={setCurrentPage}
              pageTotal={pageTotal}
              updateData={() => {}}
              loading={loading}
            />
          )}
        </TableFooter>
      </TableWrapper>
    </Modal>
  );
};

ModalWeatherTable.propTypes = {
  visible: PropTypes.bool.isRequired,
  setVisible: PropTypes.func.isRequired,
  user: PropTypes.object
};

const TitleGroup = styled.div`
  display: flex;
  align-items: baseline;
  gap: ${pad / 2}px;
  min-width: 0;
`;

const Title = styled(Heading)`
  white-space: nowrap;
`;

// Hidden on mobile. Only the (often long) ACIS station name truncates, so the
// distance stays visible; the full text is on hover.
const StationNote = styled(Small)`
  display: none;
  min-width: 0;
  white-space: nowrap;

  ${bp(3)} {
    display: flex;
  }
`;

const StationName = styled.span`
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const HeaderRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: ${pad}px;
  width: 100%;

  ${bp(3)} {
    flex-wrap: nowrap;
  }
`;

const Controls = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: ${pad}px;
  min-width: 0;

  ${bp(3)} {
    flex-wrap: nowrap;
    flex-shrink: 0;
  }
`;

// Keeps each label with its date field when the controls wrap on mobile.
const DatePart = styled.div`
  display: flex;
  flex-wrap: nowrap;
  align-items: center;
  gap: ${pad / 2}px;
  white-space: nowrap;

  /* InputDate FormFieldWrapper defaults to width: 100% */
  & > div {
    width: auto;
    margin-top: 0;
    flex-shrink: 0;
  }
`;

const FooterLeft = styled(Inline)`
  flex-wrap: wrap;
  gap: ${pad}px;
`;

const Export = styled(Button)`
  padding: ${pad / 2}px ${pad}px;
  border-radius: ${radius};
  background: ${({theme}) => theme.secondary};
  color: ${({theme}) => theme.tertiary};
  flex-shrink: 0;
`;

export default ModalWeatherTable;
